import React, { useState, useEffect } from 'react';
import { useEventFilter } from 'context/events/EventFilter';
import { useUserLocation } from 'context/user/UserLocation';
import tagsApi from 'api/requests/tags';
import { transformedDateFilters } from 'theme/filters';
import eventTypes from 'theme/eventTypes';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';
import FilterSheet, {
    ChipsWrap,
    FilterChip,
    FilterLoading,
    FilterRange,
    FilterSection,
} from 'modules/_common/filters/FilterSheet';

const MIN_RANGE = 1;
const MAX_RANGE = 50;
const DEFAULT_RANGE = 25;

// "Todos" é a ausência de filtro, e não um id que a API precise receber.
const TYPE_OPTIONS = [
    { value: null, label: 'Todos' },
    { value: eventTypes.free.id, label: eventTypes.free.label },
    { value: eventTypes.paid.id, label: eventTypes.paid.label },
];

export default function FiltersModal({ isOpen, onClose }) {
    const { eventFilter, setEventFilter } = useEventFilter();
    const { userLocation } = useUserLocation();

    const [distance, setDistance] = useState(eventFilter?.distance_range || DEFAULT_RANGE);
    const [date, setDate] = useState(eventFilter?.dates || null);
    const [eventType, setEventType] = useState(eventFilter?.classification_id || null);
    const [selectedCategories, setSelectedCategories] = useState(eventFilter?.categories || []);
    const [tags, setTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchTags();
            setDistance(eventFilter?.distance_range || DEFAULT_RANGE);
            setDate(eventFilter?.dates || null);
            setEventType(eventFilter?.classification_id || null);
            setSelectedCategories(eventFilter?.categories || []);
        }
    }, [isOpen]);

    async function fetchTags() {
        setLoadingTags(true);
        try {
            const { data } = await tagsApi.get(1);
            setTags(data || []);
        } catch (error) {
            console.error('Error fetching tags:', error);
        } finally {
            setLoadingTags(false);
        }
    }

    function toggleCategory(tagName) {
        setSelectedCategories(prev =>
            prev.includes(tagName)
                ? prev.filter(c => c !== tagName)
                : [...prev, tagName]
        );
    }

    // Clicar de novo no período ativo desmarca: é o único jeito de voltar a "qualquer data".
    function toggleDate(value) {
        setDate(prev => prev === value ? null : value);
    }

    function handleClear() {
        setDistance(DEFAULT_RANGE);
        setDate(null);
        setEventType(null);
        setSelectedCategories([]);
        setEventFilter(null);
        onClose();
    }

    const hasLocation = !!(userLocation?.lat && userLocation?.long);

    const hasLocalFilters = !!(date || eventType || selectedCategories.length > 0 ||
        (userLocation?.lat && distance !== DEFAULT_RANGE));

    function handleApply() {
        const hasFilters = hasLocalFilters;

        setEventFilter(hasFilters ? {
            distance_range: userLocation?.lat ? distance : null,
            dates: date,
            classification_id: eventType,
            categories: selectedCategories,
        } : null);

        onClose();
    }

    const categoriesHint = selectedCategories.length
        ? `${selectedCategories.length} selecionada${selectedCategories.length > 1 ? 's' : ''}`
        : undefined;

    return (
        <FilterSheet
            isOpen={isOpen}
            onClose={onClose}
            canClear={hasLocalFilters || !!eventFilter}
            onClear={handleClear}
            onApply={handleApply}
        >
            {hasLocation && (
                <FilterSection title='Distância' hint={`${distance} km`}>
                    <FilterRange
                        value={distance}
                        min={MIN_RANGE}
                        max={MAX_RANGE}
                        onChange={setDistance}
                    />
                </FilterSection>
            )}

            <FilterSection title='Quando'>
                <ChipsWrap>
                    {transformedDateFilters.map(opt => (
                        <FilterChip
                            key={opt.value}
                            label={opt.label}
                            active={date === opt.value}
                            onClick={() => toggleDate(opt.value)}
                        />
                    ))}
                </ChipsWrap>
            </FilterSection>

            <FilterSection title='Tipo'>
                <ChipsWrap>
                    {TYPE_OPTIONS.map(opt => (
                        <FilterChip
                            key={String(opt.value)}
                            label={opt.label}
                            active={String(eventType ?? null) === String(opt.value)}
                            onClick={() => setEventType(opt.value)}
                        />
                    ))}
                </ChipsWrap>
            </FilterSection>

            <FilterSection title='Categorias' hint={categoriesHint}>
                {loadingTags ? (
                    <FilterLoading>Carregando...</FilterLoading>
                ) : (
                    <ChipsWrap>
                        {tags.map(tag => {
                            const TagIcon = getCategoryIcon(tag.icon);
                            return (
                                <FilterChip
                                    key={tag.id}
                                    label={tag.name}
                                    icon={<TagIcon />}
                                    active={selectedCategories.includes(tag.name)}
                                    onClick={() => toggleCategory(tag.name)}
                                />
                            );
                        })}
                    </ChipsWrap>
                )}
            </FilterSection>
        </FilterSheet>
    );
}
